import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { GlobeHemisphereEastIcon, TruckIcon } from '@phosphor-icons/react'

import { Card, CardTitle } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'

import BestSellingProductsOriginChart from './BestSellingProductsOriginChart/BestSellingProductsOriginChart'
import CountriesCard from './CountriesCard/CountriesCard'
import DeliveryTimeByCarrierChart from './DeliveryTimeByCarrierChart/DeliveryTimeByCarrierChart'
import GlobalInfo from './GlobalInfo/GlobalInfo'
import { getCountryCodeByRegionId } from './hooks/useGeneralMetrics/helpers'
import useGeneralMetrics from './hooks/useGeneralMetrics/useGeneralMetrics'
import { viewInfoTypes } from './constants'
import * as Styles from './styles'

const countriesAdditionalFilters = { regionId: 'all' }

const ShipmentsMetricsContent = ({ commonFilters, setCommonFilters, regions, fetchingRegions }) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { data: generalMetrics, loading: fetchingGeneralMetrics } = useMetrics({
    storeId: info?.selectedStore?.id,
    service: metricsServices.getShipmentsGeneralMetrics,
    filters: commonFilters,
  })
  const { data: countriesMetricsData, loading: fetchingCountriesMetrics } = useMetrics({
    storeId: info?.selectedStore?.id,
    service: metricsServices.getShipmentsCountriesMetrics,
    filters: commonFilters,
    additionalFilters: countriesAdditionalFilters,
  })
  const { data: bestSellingProductsOriginData, loading: fetchingBestSellingProductsOriginMetrics } = useMetrics({
    storeId: info?.selectedStore?.id,
    service: metricsServices.getShipmentsBestSellingProductsOriginMetrics,
    filters: commonFilters,
  })
  const { data: deliveryTimeByCarrierData, loading: fetchingDeliveryTimeByCarrierMetrics } = useMetrics({
    storeId: info?.selectedStore?.id,
    service: metricsServices.getShipmentsDeliveryTimeByCarrierMetrics,
    filters: commonFilters,
  })
  const countriesMetrics = countriesMetricsData?.results || []
  const bestSellingProductsOriginMetrics = bestSellingProductsOriginData?.results || []
  const deliveryTimeByCarrierMetrics = deliveryTimeByCarrierData?.results || []
  const { infoType, onChangeCountry, viewType, onChangeViewType, heatMapMetrics, orderBy, onChangeOrderBy } =
    useGeneralMetrics({
      regions,
      setCommonFilters,
      commonFilters,
      countriesMetrics,
    })
  const countryCodeSelected = getCountryCodeByRegionId(regions, commonFilters?.regionId)

  return (
    <Styles.MainContainer>
      {viewType === viewInfoTypes.list && (
        <GlobalInfo
          metrics={generalMetrics}
          regions={regions}
          currency={info?.selectedStore?.currency}
          viewType={viewType}
        />
      )}
      <CountriesCard
        viewType={viewType}
        onChangeViewType={onChangeViewType}
        orderBy={orderBy}
        onChangeOrderBy={onChangeOrderBy}
        onChangeCountry={onChangeCountry}
        countryCodeSelected={countryCodeSelected}
        heatMapMetrics={heatMapMetrics}
        currency={info?.selectedStore?.currency}
        generalMetrics={generalMetrics}
        fetchingGeneralMetrics={fetchingGeneralMetrics}
        fetchingRegions={fetchingRegions}
        regions={regions}
        infoType={infoType}
        fetchingCountriesMetrics={fetchingCountriesMetrics}
        countriesMetrics={countriesMetrics}
      />
      <Styles.CountriesAndCarriersContainer>
        <Card showBorder={false}>
          <CardTitle>
            <Styles.CardTitleContainer>
              <GlobeHemisphereEastIcon />
              {t('Países de origen de los productos más vendidos')}
            </Styles.CardTitleContainer>
          </CardTitle>
          <BestSellingProductsOriginChart
            metrics={bestSellingProductsOriginMetrics}
            loading={fetchingBestSellingProductsOriginMetrics}
            regions={regions}
            onClickCountry={onChangeCountry}
          />
        </Card>
        <Card showBorder={false}>
          <CardTitle>
            <Styles.CardTitleContainer>
              <TruckIcon />
              {t('Tiempo de entrega medio por servicio de transportista')}
            </Styles.CardTitleContainer>
          </CardTitle>
          <DeliveryTimeByCarrierChart
            metrics={deliveryTimeByCarrierMetrics}
            loading={fetchingDeliveryTimeByCarrierMetrics}
          />
        </Card>
      </Styles.CountriesAndCarriersContainer>
    </Styles.MainContainer>
  )
}

export default ShipmentsMetricsContent
